<script setup lang="ts">
import { gql } from '@apollo/client/core';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useCourses } from '@/composables/queries/useCourses';
import { courseEquivalenceLabel, formatCourseInstance } from '@/utils/courseInstance';

const toast = useToast();
const confirm = useConfirm();

const GET_GROUPS = gql`
  query CourseEquivalenceGroups {
    courseEquivalenceGroups {
      equivalence_id
      courses { id course_code course_title unit }
    }
  }
`;

const LINK = gql`
  mutation LinkCourseEquivalence($course_ids: [ID!]!) {
    linkCourseEquivalence(course_ids: $course_ids) { success message }
  }
`;

const UNLINK = gql`
  mutation UnlinkCourseEquivalence($course_id: ID!) {
    unlinkCourseEquivalence(course_id: $course_id) { success message }
  }
`;

const { result, loading, refetch } = useQuery(GET_GROUPS);
const { mutate: linkMutate, loading: linking } = useMutation(LINK);
const { mutate: unlinkMutate } = useMutation(UNLINK);

const groups = computed(() => result.value?.courseEquivalenceGroups ?? []);

const showLinkDialog = ref(false);
const selectedCourseIds = ref<string[]>([]);

const { courses, setFilter: setCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });
let courseDebounce: ReturnType<typeof setTimeout> | null = null;
function onCourseFilter(event: { originalEvent: Event; value: string }) {
  if (courseDebounce) clearTimeout(courseDebounce);
  courseDebounce = setTimeout(() => { setCourseFilter({ query: event.value }); }, 300);
}
const courseOptions = computed(() =>
  courses.value.map((c: { id: string; course_code: string; course_title: string; unit: number; course_instance_id?: string; course_instance_year?: number | null }) => ({
    label: `${formatCourseInstance(c)} — ${c.course_title} (${c.unit}u)`,
    value: c.id,
    code: c.course_code,
    title: c.course_title,
    instance: formatCourseInstance(c),
  })),
);

async function handleLink() {
  if (selectedCourseIds.value.length < 2) return;
  try {
    const res = await linkMutate({ course_ids: selectedCourseIds.value });
    const data = res?.data?.linkCourseEquivalence;
    if (data?.success) {
      toast.add({ severity: 'success', summary: 'Linked', detail: data.message, life: 3000 });
      selectedCourseIds.value = [];
      showLinkDialog.value = false;
      await refetch();
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: data?.message, life: 5000 });
    }
  } catch (e: unknown) {
    toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
  }
}

async function handleUnlink(courseId: string, courseCode: string) {
  confirm.require({
    message: `Remove ${courseCode} from its equivalence group?`,
    header: 'Remove from Group',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Remove',
    acceptClass: 'p-button-danger',
    accept: async() => {
      try {
        const res = await unlinkMutate({ course_id: courseId });
        if (res?.data?.unlinkCourseEquivalence?.success) {
          toast.add({ severity: 'success', summary: 'Removed', detail: res.data.unlinkCourseEquivalence.message, life: 3000 });
          await refetch();
        }
      } catch (e: unknown) {
        toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      }
    },
  });
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="bg-primary/10 text-primary-700 p-3 rounded text-sm flex items-start gap-2">
      <i class="pi pi-info-circle mt-0.5"></i>
      <div>
        <p class="font-semibold">Course Equivalence</p>
        <p>Link courses that are considered the same (e.g., when a course code changes across sessions). If a student passed any course in a group, all courses in that group are treated as passed — preventing false outstanding flags.</p>
      </div>
    </div>

    <div class="flex items-center gap-2">
      <Button label="Link Courses" icon="pi pi-link" size="small" @click="showLinkDialog = true" />
    </div>

    <div v-if="loading" class="text-center py-8">
      <i class="pi pi-spin pi-spinner text-2xl text-gray-400"></i>
    </div>

    <div v-else-if="groups.length === 0" class="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
      <i class="pi pi-link text-2xl mb-2 block text-gray-300"></i>
      <p class="text-sm">No equivalence groups yet. Link courses that represent the same content.</p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      <div v-for="group in groups" :key="group.equivalence_id"
        class="border rounded-lg p-3 bg-white shadow-sm">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-bold text-gray-500" data-testid="equivalence-group-label">{{ courseEquivalenceLabel(group.courses) }}</span>
          <span class="text-xs text-gray-400">{{ group.courses.length }} courses</span>
        </div>
        <div class="space-y-1.5">
          <div v-for="course in group.courses" :key="course.id"
            class="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5">
            <div>
              <span class="text-sm font-medium">{{ course.course_code }}</span>
              <span class="text-xs text-gray-500 ml-1">{{ course.course_title }}</span>
              <span class="text-xs text-gray-400 ml-1">({{ course.unit }}u)</span>
            </div>
            <Button icon="pi pi-times" text rounded severity="danger" size="small"
              @click="handleUnlink(course.id, course.course_code)" v-tooltip.left="'Remove from group'" />
          </div>
        </div>
      </div>
    </div>

    <!-- Link Dialog -->
    <Dialog v-model:visible="showLinkDialog" header="Link Equivalent Courses" modal class="w-full md:w-5/12">
      <div class="flex flex-col gap-3 py-2">
        <p class="text-sm text-gray-600">Select 2 or more courses that should be treated as equivalent. If a student passed any one of them, all are considered passed.</p>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Courses to link *</label>
          <MultiSelect v-model="selectedCourseIds" :options="courseOptions" optionLabel="label" optionValue="value"
            placeholder="Search and select courses..." filter @filter="onCourseFilter" :maxSelectedLabels="5" class="w-full">
            <template #option="{ option }">
              <div class="flex flex-col py-0.5">
                <span class="text-sm font-medium">{{ option.code }} — {{ option.title }}</span>
                <span v-if="option.instance" class="text-xs text-gray-400">{{ option.instance }}</span>
              </div>
            </template>
          </MultiSelect>
          <small class="text-gray-500">{{ selectedCourseIds.length }} course(s) selected (minimum 2)</small>
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary" text @click="showLinkDialog = false" />
        <Button label="Link Courses" icon="pi pi-link" @click="handleLink" :loading="linking"
          :disabled="selectedCourseIds.length < 2" />
      </template>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>
